import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'
import moment from 'moment'
import { ALL_HOTEL_TYPES } from 'constants/offer'
import { queryKeyTypeaheadId } from 'constants/url'
import GeoContext from 'contexts/geoContext'
import { getOffersById } from 'api/offer'
import { uniqueBy } from 'lib/array/arrayUtils'
import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import { encodeSearchParams, serialiseOccupancy } from 'lib/search/searchUtils'
import uuidV4 from 'lib/string/uuidV4Utils'
import { buildUrl } from 'lib/url/searchUrlUtils'
import { getOfferDetail } from 'tripPlanner/api'
import type { HotelSearchSection, TripItineraryData } from 'tripPlanner/api/types'
import { useSearchPlace } from 'tripPlanner/hooks/api/place'
import { OFFER_DETAIL_STALE_TIME } from 'tripPlanner/hooks/api/queryConstants'
import { useOfferBookingState } from 'tripPlanner/hooks/useOfferBookingState'
import useTripPlannerOfferListTracking from 'tripPlanner/hooks/useTripPlannerOfferListTracking'
import * as OfferDetailKeys from 'tripPlanner/hooks/reactQueryKeys/offerDetail'
import * as OfferKeys from 'tripPlanner/hooks/reactQueryKeys/offers'
import {
  getOfferItineraryDayRange,
  getOfferSavedBooking,
  getSavedHotelItemsFromStops,
  isOfferBookedInItinerary,
  type SavedTripHotelItem,
} from 'tripPlanner/utils/chatItinerary'
import useOfferDetails from '../../hooks/useOfferDetails'
import useOfferReviews from '../../hooks/useOfferReviews'
import { applyOfferSummaryMemberPriceFallback, applySavedHotelPriceFallback, mapOfferDetailToDrawerHotel, mapOfferToDrawerHotelTileFeatures, mergeDrawerHotelWithOfferDetail } from './mapOfferDetailToDrawerHotel'
import { composeLocationString, mapGeoLocation, mapImages, mapPrice } from './mapToDrawerHotel'
import type { DrawerHotel, HotelListingDrawerProps } from './types'
import useHotelSearch from './useHotelSearch'
import {
  areHotelSearchDateSelectionsEqual,
  getDefaultHotelSearchDateSelection,
  getHotelSearchDateParams,
  getHotelSearchDateSelectionKey,
} from './hotelSearchDates'
import type { ListingDrawerHotelDateSelection } from '../ListingDrawerSearchDates'

const DEFAULT_OCCUPANCIES: Array<App.Occupants> = [{ adults: 2 }]
export const HOTEL_DRAWER_MAX_GUESTS = 12
export const HOTEL_DRAWER_MAX_WIDTH_PX = 1200

function isHotelSearchType(offerType?: string): offerType is App.OfferType {
  return offerType !== undefined && ALL_HOTEL_TYPES.some((hotelSearchType) => hotelSearchType === offerType)
}

function isTileSummaryOffer(
  offer: Awaited<ReturnType<typeof getOffersById>>[number],
): offer is App.HotelOffer | App.BedbankOffer {
  return isLEHotel(offer) || isBedbankOffer(offer)
}

function normalizePlaceName(value?: string) {
  return value?.trim().toLowerCase()
}

function getSearchItemPlaceId(searchItem?: App.SearchItem): string | undefined {
  if (searchItem?.searchType === 'destination' && searchItem.value !== searchItem.format.mainText) {
    return searchItem.value
  }

  if (
    searchItem?.searchType === 'searchItemV3' &&
    'placeIds' in searchItem.searchItem.extractedData &&
    searchItem.searchItem.extractedData.placeIds?.length
  ) {
    return searchItem.searchItem.extractedData.placeIds[0]
  }

  return undefined
}

function getSearchItemName(searchItem?: App.SearchItem): string | undefined {
  return searchItem?.format.mainText
}

function cloneOccupancies(occupancies: Array<App.Occupants>) {
  return occupancies.map((room) => ({
    ...room,
    childrenAge: room.childrenAge ? [...room.childrenAge] : undefined,
  }))
}

function getSavedHotelOfferIds(stops: TripItineraryData['stops'] | undefined): Set<string> {
  return new Set(getSavedHotelItemsFromStops(stops).map((item) => item.savedItemData.offerId))
}

function getSavedHotelNights(savedData: SavedTripHotelItem['savedItemData']): number | undefined {
  if (!savedData.checkIn || !savedData.checkOut) return undefined
  return Math.max(1, moment(savedData.checkOut).diff(moment(savedData.checkIn), 'days'))
}

function getSavedHotelLocation(savedData: SavedTripHotelItem['savedItemData'], fallbackLocation: string): string {
  return composeLocationString(savedData.location?.city, savedData.location?.country) || fallbackLocation
}

function getDefaultCheckIn(stopStartDate: string | undefined, contextStop: HotelListingDrawerProps['contextStop']) {
  if (stopStartDate) return stopStartDate
  return contextStop?.days.find(d => d.date)?.date
}

function getDefaultCheckOut(stopEndDate: string | undefined, contextStop: HotelListingDrawerProps['contextStop']) {
  if (stopEndDate) return stopEndDate
  if (!contextStop?.days.length) return undefined
  const datedDays = contextStop.days.filter(d => !!d.date)
  return datedDays.length ? datedDays[datedDays.length - 1].date : undefined
}

function mapSavedHotelItemToDrawerHotel(item: SavedTripHotelItem, fallbackLocation: string): DrawerHotel {
  const savedData = item.savedItemData
  const savedImages = mapImages(savedData.imageIds ?? [])

  return {
    id: `saved-${item.id}-${savedData.offerId}`,
    offerId: savedData.offerId,
    offerType: savedData.offerType,
    name: item.name,
    location: getSavedHotelLocation(savedData, fallbackLocation),
    images: savedImages,
    price: mapPrice(savedData.price, getSavedHotelNights(savedData), savedData.currencyCode),
    luxPlusPrice: savedData.memberPrice,
    value: savedData.value,
    discountPercent: savedData.discountPercent,
    memberDiscountPercent: savedData.memberDiscountPercent,
    saleUnit: savedData.saleUnit,
    checkIn: savedData.checkIn,
    checkOut: savedData.checkOut,
    geoLocation: mapGeoLocation(savedData.location?.geoLocation),
  }
}

// Explore/saved tiles should prefer public-offers images (same source as regular
// hotel search). svc-trip search `imageIds` can diverge after accommodation-
// content enrichment. Until public-offers arrive, keep the existing images.
export function preferPublicOfferImages(
  hotel: DrawerHotel,
  summaryOffer: App.HotelOffer | App.BedbankOffer | undefined,
): DrawerHotel {
  if (!summaryOffer?.images?.length) {
    return hotel
  }

  return {
    ...hotel,
    images: summaryOffer.images,
  }
}

// Explore tiles must use public-offers images. Return undefined while that
// hotel's public-offers lookup is still pending so it is withheld from the list
// instead of flashing the wrong hero image.
export function withPublicOfferTileImages(
  hotel: DrawerHotel,
  summaryOffer: App.HotelOffer | App.BedbankOffer | undefined,
  isPublicOfferLookupPending: boolean,
): DrawerHotel | undefined {
  if (summaryOffer?.images?.length) {
    return {
      ...hotel,
      images: summaryOffer.images,
    }
  }

  if (summaryOffer) {
    // Lookup resolved for this hotel but returned no usable images — fall through
    // to svc-trip rather than withholding (avoids hiding a visible tile during
    // a load-more refetch triggered by other hotels).
    return hotel
  }

  if (isPublicOfferLookupPending) {
    return undefined
  }

  // Lookup finished without images — keep svc-trip as last resort.
  return hotel
}

export function getExploreListLoadingState({
  isHotelSearchLoading,
  isHotelSearchLoadingMore,
  exploreSearchCount,
  visibleExploreCount,
  isPublicOfferLookupPending,
}: {
  isHotelSearchLoading: boolean
  isHotelSearchLoadingMore: boolean
  exploreSearchCount: number
  visibleExploreCount: number
  isPublicOfferLookupPending: boolean
}): { isLoading: boolean; isLoadingMore: boolean } {
  // Hold explore tiles behind one list-level loading state until public-offers
  // images are ready — same idea as regular search waiting on offer fetch.
  const isInitialPublicOfferWait = isPublicOfferLookupPending &&
    exploreSearchCount > 0 &&
    visibleExploreCount === 0
  const isAppendPublicOfferWait = isPublicOfferLookupPending &&
    visibleExploreCount > 0 &&
    exploreSearchCount > visibleExploreCount

  return {
    isLoading: isHotelSearchLoading || isInitialPublicOfferWait,
    isLoadingMore: isHotelSearchLoadingMore || (
      !isHotelSearchLoading && isAppendPublicOfferWait
    ),
  }
}

// Saved itinerary data only carries the fields captured at save time, so a saved
// row can be missing images, rating/reviews, inclusions/amenities, deeplink/map
// metadata, etc. When the same offer is still in the live search results, fill any
// gaps from that tile so saving a hotel doesn't make the visible card regress.
// We never drop a saved row for missing images - the canvas counts saved hotels
// from itinerary data, so hiding one here would cause a "1 saved" / empty-drawer mismatch.
function applySearchResultDisplayFallback(hotel: DrawerHotel, fallbackHotelsByOfferId: Map<string, DrawerHotel>): DrawerHotel {
  const fallback = fallbackHotelsByOfferId.get(hotel.offerId)
  if (!fallback) return hotel

  return {
    ...hotel,
    images: hotel.images.length ? hotel.images : fallback.images,
    price: hotel.price ?? fallback.price,
    luxPlusPrice: hotel.luxPlusPrice ?? fallback.luxPlusPrice,
    value: hotel.value ?? fallback.value,
    discountPercent: hotel.discountPercent ?? fallback.discountPercent,
    memberDiscountPercent: hotel.memberDiscountPercent ?? fallback.memberDiscountPercent,
    saleUnit: hotel.saleUnit ?? fallback.saleUnit,
    rating: hotel.rating ?? fallback.rating,
    reviewsTotal: hotel.reviewsTotal ?? fallback.reviewsTotal,
    inclusions: hotel.inclusions?.length ? hotel.inclusions : fallback.inclusions,
    tileAmenities: hotel.tileAmenities?.length ? hotel.tileAmenities : fallback.tileAmenities,
    amenities: hotel.amenities?.length ? hotel.amenities : fallback.amenities,
    deeplinkUrl: hotel.deeplinkUrl ?? fallback.deeplinkUrl,
    mapUrl: hotel.mapUrl ?? fallback.mapUrl,
    geoLocation: hotel.geoLocation ?? fallback.geoLocation,
  }
}

type UseHotelListingDrawerDataOptions = Pick<
  HotelListingDrawerProps,
  | 'isOpen'
  | 'onClose'
  | 'placeName'
  | 'placeCountry'
  | 'stopStartDate'
  | 'stopEndDate'
  | 'stopCoordinates'
  | 'contextStop'
  | 'itineraryData'
  | 'initialSelectedOfferId'
  | 'initialSelectedOfferType'
>

export default function useHotelListingDrawerData({
  isOpen,
  onClose,
  placeName,
  placeCountry,
  stopStartDate,
  stopEndDate,
  stopCoordinates,
  contextStop,
  itineraryData,
  initialSelectedOfferId,
  initialSelectedOfferType,
}: UseHotelListingDrawerDataOptions) {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const [selectedOfferId, setSelectedOfferId] = useState<string | undefined>(undefined)
  const [selectedOfferType, setSelectedOfferType] = useState<string | undefined>(undefined)
  // True only when the detail view was reached by selecting a hotel from the
  // listing, so we know whether "back" has a listing to return to. When the drawer
  // is opened straight into a hotel (initialSelectedOfferId, e.g. from the timeline)
  // there is no previous drawer, so the header shows a close (X) instead of Back.
  const [detailFromListing, setDetailFromListing] = useState(false)
  const [selectedHotelName, setSelectedHotelName] = useState<string | undefined>(undefined)
  const [typeaheadId] = useState(() => uuidV4())
  const [drawerSessionKey, setDrawerSessionKey] = useState(() => uuidV4())
  const [searchLocation, setSearchLocation] = useState(() => `${placeName}, ${placeCountry}`)
  const [hotelDateSelection, setHotelDateSelection] = useState<ListingDrawerHotelDateSelection>(() =>
    getDefaultHotelSearchDateSelection(
      getDefaultCheckIn(stopStartDate, contextStop),
      getDefaultCheckOut(stopEndDate, contextStop),
    ),
  )
  const [occupancies, setOccupancies] = useState<Array<App.Occupants>>(() => cloneOccupancies(DEFAULT_OCCUPANCIES))
  const [selectedSearchItem, setSelectedSearchItem] = useState<App.SearchItem | undefined>(undefined)
  const [hasLoadMoreTriggerLeftView, setHasLoadMoreTriggerLeftView] = useState(false)
  const wasOpenRef = useRef(false)
  const defaultSearchQuery = `${placeName}, ${placeCountry}`
  const searchQuery = searchLocation.trim() || defaultSearchQuery
  const defaultSerializedOccupancies = useMemo(() => serialiseOccupancy(DEFAULT_OCCUPANCIES), [])
  const selectedSearchItemPlaceId = useMemo(() => getSearchItemPlaceId(selectedSearchItem), [selectedSearchItem])
  const selectedSearchItemName = useMemo(() => getSearchItemName(selectedSearchItem), [selectedSearchItem])

  useEffect(() => {
    if (!isOpen || !initialSelectedOfferId) return

    setSelectedOfferId(initialSelectedOfferId)
    setSelectedOfferType(initialSelectedOfferType)
    // Opened straight into detail - there is no listing behind it to go back to.
    setDetailFromListing(false)
    setSelectedHotelName(undefined)
  }, [initialSelectedOfferId, initialSelectedOfferType, isOpen])

  const defaultCheckIn = useMemo(() => {
    return getDefaultCheckIn(stopStartDate, contextStop)
  }, [stopStartDate, contextStop])

  const defaultCheckOut = useMemo(() => {
    return getDefaultCheckOut(stopEndDate, contextStop)
  }, [stopEndDate, contextStop])
  const defaultHotelDateSelection = useMemo(
    () => getDefaultHotelSearchDateSelection(defaultCheckIn, defaultCheckOut),
    [defaultCheckIn, defaultCheckOut],
  )
  const previousDefaultsRef = useRef({
    hotelDateSelection: defaultHotelDateSelection,
    searchQuery: defaultSearchQuery,
  })

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setDrawerSessionKey(uuidV4())
      setSearchLocation(defaultSearchQuery)
      setHotelDateSelection(defaultHotelDateSelection)
      setOccupancies(cloneOccupancies(DEFAULT_OCCUPANCIES))
      setSelectedSearchItem(undefined)
    }

    wasOpenRef.current = isOpen
  }, [defaultHotelDateSelection, defaultSearchQuery, isOpen])

  useEffect(() => {
    const previousDefaults = previousDefaultsRef.current

    if (isOpen && wasOpenRef.current) {
      if (normalizePlaceName(searchLocation) === normalizePlaceName(previousDefaults.searchQuery)) {
        setSearchLocation(defaultSearchQuery)
        setSelectedSearchItem(undefined)
      }

      setHotelDateSelection((currentSelection) =>
        areHotelSearchDateSelectionsEqual(currentSelection, previousDefaults.hotelDateSelection)
          ? defaultHotelDateSelection
          : currentSelection,
      )
    }

    previousDefaultsRef.current = {
      hotelDateSelection: defaultHotelDateSelection,
      searchQuery: defaultSearchQuery,
    }
  }, [defaultHotelDateSelection, defaultSearchQuery, isOpen, searchLocation])

  const { checkIn, checkOut, flexibleMonths, flexibleNights } = getHotelSearchDateParams(hotelDateSelection)
  const serializedOccupancies = useMemo(() => serialiseOccupancy(occupancies), [occupancies])
  const offerDetailOccupancies = serializedOccupancies.join(',')
  const isDefaultDestinationSearch = normalizePlaceName(searchQuery) === normalizePlaceName(defaultSearchQuery)
  const hotelSearchQuery = isDefaultDestinationSearch
    ? `Hotels in ${defaultSearchQuery}`
    : searchQuery
  const isRefinedSearch = !isDefaultDestinationSearch ||
    !areHotelSearchDateSelectionsEqual(hotelDateSelection, defaultHotelDateSelection) ||
    serializedOccupancies.join('|') !== defaultSerializedOccupancies.join('|')
  const headerTitle = isRefinedSearch ?
      intl.formatMessage({
        id: 'uDktRbWrV6na2m15vYXA',
        defaultMessage: 'Search hotels',
        description: 'Generic title for hotel listing drawer after users refine the drawer search.',
      }) :
    undefined

  const filterNights = useMemo(() => {
    if (!checkIn || !checkOut) return undefined
    const nights = moment(checkOut).diff(moment(checkIn), 'days')
    return nights > 0 ? nights : undefined
  }, [checkIn, checkOut])

  const { data: destinationPlace, isFetched: isDestinationPlaceFetched } = useSearchPlace(
    {
      lat: stopCoordinates?.lat,
      lng: stopCoordinates?.lng,
      type: 'city',
    },
    {
      enabled: isOpen && !!stopCoordinates,
      staleTime: Infinity,
    },
  )

  const defaultDestinationPlaceId = isDefaultDestinationSearch ? destinationPlace?.id : undefined
  const searchPlaceId = selectedSearchItemPlaceId ?? defaultDestinationPlaceId
  const isHotelSearchReady = !isDefaultDestinationSearch || !stopCoordinates || isDestinationPlaceFetched

  const {
    hotels,
    sections: hotelSearchSections,
    isLoading: isHotelSearchLoading,
    isError: isSearchError,
    hasMore,
    isLoadingMore: isHotelSearchLoadingMore,
    loadMore,
    refetch: refetchSearch,
  } = useHotelSearch({
    searchQuery: hotelSearchQuery,
    placeId: searchPlaceId,
    region: geo.currentRegionCode,
    checkIn,
    checkOut,
    flexibleMonths,
    flexibleNights,
    occupancies,
    enabled: isOpen && isHotelSearchReady,
  })
  const hotelOfferIds = useMemo(() => hotels.map((hotel) => hotel.offerId), [hotels])
  const shouldLoadTileSummaryOffers = hotelDateSelection.type !== 'flexible'
  const {
    data: tileOffers = [],
    isFetching: isTileOffersFetching,
  } = useQuery({
    queryKey: OfferKeys.collection(geo.currentRegionCode, [
      checkIn ?? '',
      checkOut ?? '',
      flexibleMonths ?? '',
      flexibleNights ?? '',
      serializedOccupancies.join('|'),
      ...hotelOfferIds,
    ]),
    queryFn: () => getOffersById(
      hotelOfferIds,
      geo.currentRegionCode,
      {
        checkIn,
        checkOut,
        occupancies,
      },
    ),
    enabled: isOpen && hotelOfferIds.length > 0 && shouldLoadTileSummaryOffers,
    staleTime: OFFER_DETAIL_STALE_TIME,
    keepPreviousData: shouldLoadTileSummaryOffers,
  })
  // Pending only while a fetch for the current explore offer IDs is in flight.
  // Hotels that already have matching public-offer images (e.g. page 1 during
  // load-more with keepPreviousData) still render via withPublicOfferTileImages.
  const isPublicOfferLookupPending = hotelOfferIds.length > 0 && isTileOffersFetching
  const tileSummaryOffers = useMemo<Array<App.HotelOffer | App.BedbankOffer>>(
    () => shouldLoadTileSummaryOffers ? tileOffers.filter(isTileSummaryOffer) : [],
    [shouldLoadTileSummaryOffers, tileOffers],
  )
  const trackingOfferById = useMemo(() => {
    return new Map(tileSummaryOffers.map((offer) => [offer.id, offer]))
  }, [tileSummaryOffers])
  const tileDetailHotels = useMemo(
    () => hotels.filter((hotel): hotel is DrawerHotel & { offerType: App.OfferType } => isHotelSearchType(hotel.offerType)),
    [hotels],
  )
  const tileDetailQueries = useQueries({
    queries: tileDetailHotels.map((hotel) => ({
      queryKey: OfferDetailKeys.detail(
        hotel.offerId,
        geo.currentRegionCode,
        undefined,
        hotel.offerType,
        checkIn,
        checkOut,
        offerDetailOccupancies,
        flexibleMonths,
        flexibleNights,
      ),
      queryFn: () => getOfferDetail({
        offerId: hotel.offerId,
        region: geo.currentRegionCode,
        offerType: hotel.offerType,
        checkIn,
        checkOut,
        occupancies: offerDetailOccupancies,
        flexibleMonths,
        flexibleNights,
      }),
      enabled: isOpen,
      staleTime: OFFER_DETAIL_STALE_TIME,
    })),
  })
  const hotelsWithTileDetails = useMemo(() => {
    const summaryTileFeaturesByOfferId = new Map<string, Pick<DrawerHotel, 'inclusions' | 'tileAmenities'>>(
      tileSummaryOffers.map((offer) => [
        offer.id,
        mapOfferToDrawerHotelTileFeatures(offer),
      ]),
    )
    const detailsByOfferId = new Map<string, Pick<DrawerHotel, 'rating' | 'reviewsTotal' | 'inclusions' | 'amenities'>>()

    tileDetailQueries.forEach((query, index) => {
      const hotel = tileDetailHotels[index]
      if (!hotel || !query.data) {
        return
      }

      const detailHotel = mergeDrawerHotelWithOfferDetail(hotel, query.data)

      detailsByOfferId.set(hotel.offerId, {
        rating: detailHotel.rating,
        reviewsTotal: detailHotel.reviewsTotal,
        inclusions: detailHotel.inclusions,
        amenities: detailHotel.amenities,
      })
    })

    return hotels.flatMap((hotel) => {
      const summaryTileFeatures = summaryTileFeaturesByOfferId.get(hotel.offerId)
      const details = detailsByOfferId.get(hotel.offerId)
      const enrichedHotel = details || summaryTileFeatures ? {
        ...hotel,
        rating: details?.rating ?? hotel.rating,
        reviewsTotal: details?.reviewsTotal ?? hotel.reviewsTotal,
        inclusions: details?.inclusions?.length ? details.inclusions : summaryTileFeatures?.inclusions ?? hotel.inclusions,
        tileAmenities: summaryTileFeatures?.tileAmenities ?? hotel.tileAmenities,
        amenities: details?.amenities ?? hotel.amenities,
      } : hotel

      const hotelWithImages = withPublicOfferTileImages(
        enrichedHotel,
        trackingOfferById.get(hotel.offerId),
        isPublicOfferLookupPending,
      )

      return hotelWithImages ? [hotelWithImages] : []
    })
  }, [hotels, isPublicOfferLookupPending, tileDetailHotels, tileDetailQueries, tileSummaryOffers, trackingOfferById])

  const savedHotelStops = useMemo(() => {
    if (isRefinedSearch) return []
    return contextStop ? [contextStop] : itineraryData?.stops
  }, [contextStop, isRefinedSearch, itineraryData?.stops])

  const savedHotelDetailHotels = useMemo(
    () =>
      getSavedHotelItemsFromStops(savedHotelStops)
        .map((item) => ({ offerId: item.savedItemData.offerId, offerType: item.savedItemData.offerType }))
        .filter((hotel) => isHotelSearchType(hotel.offerType)),
    [savedHotelStops],
  )

  const savedDetailQueries = useQueries({
    queries: savedHotelDetailHotels.map((hotel) => ({
      queryKey: OfferDetailKeys.detail(
        hotel.offerId,
        geo.currentRegionCode,
        undefined,
        hotel.offerType,
        checkIn,
        checkOut,
        offerDetailOccupancies,
        flexibleMonths,
        flexibleNights,
      ),
      queryFn: () => getOfferDetail({
        offerId: hotel.offerId,
        region: geo.currentRegionCode,
        offerType: hotel.offerType,
        checkIn,
        checkOut,
        occupancies: offerDetailOccupancies,
        flexibleMonths,
        flexibleNights,
      }),
      enabled: isOpen,
      staleTime: OFFER_DETAIL_STALE_TIME,
    })),
  })

  const savedHotelDetailDrawerById = useMemo(() => {
    const detailsByOfferId = new Map<string, DrawerHotel>()
    savedDetailQueries.forEach((query, index) => {
      const hotel = savedHotelDetailHotels[index]
      if (!hotel || !query.data) return
      detailsByOfferId.set(hotel.offerId, mapOfferDetailToDrawerHotel(query.data))
    })
    return detailsByOfferId
  }, [savedDetailQueries, savedHotelDetailHotels])

  const savedDrawerHotels = useMemo(() => {
    const fallbackLocation = [placeName, placeCountry].filter(Boolean).join(', ')
    const fallbackHotelsByOfferId = new Map(savedHotelDetailDrawerById)
    hotelsWithTileDetails.forEach((hotel) => fallbackHotelsByOfferId.set(hotel.offerId, hotel))
    return getSavedHotelItemsFromStops(savedHotelStops)
      .map((item) => mapSavedHotelItemToDrawerHotel(item, fallbackLocation))
      .map((hotel) => applySearchResultDisplayFallback(hotel, fallbackHotelsByOfferId))
      .map((hotel) => preferPublicOfferImages(hotel, trackingOfferById.get(hotel.offerId)))
  }, [hotelsWithTileDetails, placeCountry, placeName, savedHotelStops, savedHotelDetailDrawerById, trackingOfferById])

  const savedHotelOfferIds = useMemo(() => {
    return getSavedHotelOfferIds(savedHotelStops)
  }, [savedHotelStops])

  const orderedHotelsWithTileDetails = useMemo(() => {
    return hotelsWithTileDetails.filter((hotel) => !savedHotelOfferIds.has(hotel.offerId))
  }, [hotelsWithTileDetails, savedHotelOfferIds])

  const exploreSearchHotels = useMemo(
    () => hotels.filter((hotel) => !savedHotelOfferIds.has(hotel.offerId)),
    [hotels, savedHotelOfferIds],
  )

  const { isLoading, isLoadingMore } = getExploreListLoadingState({
    isHotelSearchLoading,
    isHotelSearchLoadingMore,
    exploreSearchCount: exploreSearchHotels.length,
    visibleExploreCount: orderedHotelsWithTileDetails.length,
    isPublicOfferLookupPending,
  })

  const orderedHotelSearchSections = useMemo<Array<HotelSearchSection>>(() => {
    const visibleOfferIds = new Set(orderedHotelsWithTileDetails.map((hotel) => hotel.offerId))
    const visibleSearchResultPositions = hotels
      .map((hotel, index) => visibleOfferIds.has(hotel.offerId) ? index : undefined)
      .filter((index): index is number => index !== undefined)

    const visibleSections = hotelSearchSections
      .map((section) => ({
        ...section,
        position: visibleSearchResultPositions.filter((position) => position < section.position).length,
      }))
      .filter((section) => section.position < orderedHotelsWithTileDetails.length)

    return uniqueBy(
      visibleSections,
      (section) => `${section.type}:${section.position}`,
    )
  }, [
    hotelSearchSections,
    hotels,
    orderedHotelsWithTileDetails,
  ])

  const savedBooking = useMemo(
    () => getOfferSavedBooking(selectedOfferId, itineraryData),
    [selectedOfferId, itineraryData],
  )

  const { savedBookedRooms, detailOrderId, orderIdForLink } = useOfferBookingState(
    selectedOfferId ?? undefined,
    itineraryData,
  )

  const { offerDetail, isLoading: isDetailLoading, isError: isDetailError, refetch: refetchDetail } = useOfferDetails({
    offerId: selectedOfferId,
    region: geo.currentRegionCode,
    offerType: selectedOfferType,
    checkIn,
    checkOut,
    flexibleMonths,
    flexibleNights,
    occupancies: offerDetailOccupancies,
    orderId: detailOrderId,
    enabled: isOpen && !!selectedOfferId,
  })

  const { reviews, isLoading: reviewsLoading } = useOfferReviews({
    offerId: selectedOfferId,
    region: geo.currentRegionCode,
    enabled: !!selectedOfferId && !!offerDetail,
  })

  const detailSummaryOffer = useMemo(
    () => tileSummaryOffers.find(offer => offer.id === selectedOfferId),
    [tileSummaryOffers, selectedOfferId],
  )

  const detailHotel = useMemo(() => {
    if (!offerDetail) return undefined
    let hotel = mapOfferDetailToDrawerHotel(offerDetail)
    if (hotelDateSelection.type === 'anytime') {
      hotel = applyOfferSummaryMemberPriceFallback(hotel, detailSummaryOffer)
    }
    hotel = applySavedHotelPriceFallback(hotel, itineraryData)
    return hotel
  }, [detailSummaryOffer, hotelDateSelection.type, itineraryData, offerDetail])

  const selectedHotelTitle = useMemo(() => {
    if (!selectedOfferId) return undefined
    return detailHotel?.name ??
      selectedHotelName ??
      savedDrawerHotels.find((hotel) => hotel.offerId === selectedOfferId)?.name ??
      orderedHotelsWithTileDetails.find((hotel) => hotel.offerId === selectedOfferId)?.name
  }, [
    detailHotel?.name,
    orderedHotelsWithTileDetails,
    savedDrawerHotels,
    selectedHotelName,
    selectedOfferId,
  ])

  const handleSelectHotel = useCallback((hotel: DrawerHotel) => {
    setSelectedOfferId(hotel.offerId)
    setSelectedOfferType(hotel.offerType)
    setDetailFromListing(true)
    setSelectedHotelName(hotel.name)
  }, [])

  const handleBack = useCallback(() => {
    setSelectedOfferId(undefined)
    setSelectedOfferType(undefined)
    setSelectedHotelName(undefined)
    setDetailFromListing(false)
  }, [])

  const handleClose = useCallback(() => {
    setSelectedOfferId(undefined)
    setSelectedOfferType(undefined)
    setSelectedHotelName(undefined)
    setDetailFromListing(false)
    onClose()
  }, [onClose])

  const handleAfterClose = useCallback(() => {
    setSelectedOfferId(undefined)
    setSelectedOfferType(undefined)
    setSelectedHotelName(undefined)
    setDetailFromListing(false)
    setHasLoadMoreTriggerLeftView(false)
  }, [])

  const tracking = useTripPlannerOfferListTracking(
    'hotelSearchDrawer',
    `${drawerSessionKey}:${searchQuery}:${getHotelSearchDateSelectionKey(hotelDateSelection)}:${serializedOccupancies.join('|')}`,
  )

  const browseAllResultsUrl = useMemo(() => {
    const destinationId = selectedSearchItemPlaceId ?? defaultDestinationPlaceId
    const destinationName = selectedSearchItemName ?? (destinationId ? searchQuery : undefined)
    const specificDates = hotelDateSelection.type === 'specific'
      ? { checkIn: hotelDateSelection.checkIn, checkOut: hotelDateSelection.checkOut }
      : undefined
    const searchParams = encodeSearchParams({
      urlSearch: '',
      dates: specificDates,
      rooms: occupancies,
      flexibleMonths,
      flexibleNights,
      userSelectedFlexibleMonths: hotelDateSelection.type === 'flexible'
        ? hotelDateSelection.userSelectedFlexibleMonths
        : undefined,
      destinationId,
      destinationName,
    })

    if (!destinationId) {
      searchParams.set('query', searchQuery)
      searchParams.set(queryKeyTypeaheadId, typeaheadId)
    }

    return buildUrl('/search', geo.currentRegionCode, searchParams.toString())
  }, [defaultDestinationPlaceId, flexibleMonths, flexibleNights, geo.currentRegionCode, hotelDateSelection, occupancies, searchQuery, selectedSearchItemName, selectedSearchItemPlaceId, typeaheadId])

  const hasSavedHotels = savedDrawerHotels.length > 0
  const hasHotels = hasSavedHotels || orderedHotelsWithTileDetails.length > 0
  const hasExploreHotelContent = isLoading || orderedHotelsWithTileDetails.length > 0
  const canShowExploreSection = hasExploreHotelContent || hasMore
  const isDetailView = !!selectedOfferId
  // Only offer "Back" when the detail was reached from the listing; a directly
  // opened detail shows a close (X) that returns to the timeline.
  const canGoBack = isDetailView && detailFromListing
  const [loadMoreTriggerRef, isLoadMoreTriggerInView] = useInView({
    threshold: 0,
    rootMargin: '0px 0px 160px 0px',
  })

  useEffect(() => {
    if (isDetailView || !hasMore) {
      setHasLoadMoreTriggerLeftView(false)
      return
    }

    if (!isLoadMoreTriggerInView && !isLoadingMore) {
      setHasLoadMoreTriggerLeftView(true)
    }
  }, [hasMore, isDetailView, isLoadMoreTriggerInView, isLoadingMore])

  useEffect(() => {
    if (!isDetailView && hasMore && !isLoadingMore && isLoadMoreTriggerInView && hasLoadMoreTriggerLeftView) {
      setHasLoadMoreTriggerLeftView(false)
      loadMore()
    }
  }, [hasLoadMoreTriggerLeftView, hasMore, isDetailView, isLoadMoreTriggerInView, isLoadingMore, loadMore])

  const dayRangeLabel = useMemo(() => {
    if (!selectedOfferId) return undefined
    const range = getOfferItineraryDayRange(selectedOfferId, itineraryData)
    return range?.label
  }, [selectedOfferId, itineraryData])

  return {
    browseAllResultsUrl,
    canShowExploreSection,
    checkIn,
    checkOut,
    flexibleMonths,
    flexibleNights,
    hotelDateSelection,
    dayRangeLabel,
    detailHotel,
    filterNights,
    handleAfterClose,
    handleBack,
    handleClose,
    handleSelectHotel,
    hasHotels,
    hasMore,
    hasSavedHotels,
    headerTitle,
    selectedHotelTitle,
    isBooked: isOfferBookedInItinerary(selectedOfferId ?? undefined, itineraryData),
    canGoBack,
    isDetailError,
    isDetailLoading,
    isDetailView,
    isLoading,
    isSearchError,
    isLoadingMore,
    loadMore,
    loadMoreTriggerRef,
    occupancies,
    orderIdForLink,
    refetchDetail,
    refetchSearch,
    reviews,
    reviewsLoading,
    savedBookedRooms,
    savedBooking,
    savedDrawerHotels,
    savedHeading: intl.formatMessage({
      id: 'KlCMmpXwaYn9wnS-xRP-',
      defaultMessage: 'Saved',
      description: 'Section heading for saved hotels in the trip planner hotel drawer',
    }),
    searchLocation,
    setOccupancies,
    setSearchLocation,
    setHotelDateSelection,
    setSelectedSearchItem,
    orderedHotelsWithTileDetails,
    orderedHotelSearchSections,
    tracking,
    trackingOfferById,
    exploreHeading: intl.formatMessage({
      id: 'jUY_ikacrLCymlSbEXow',
      defaultMessage: 'Explore more',
      description: 'Section heading above hotel search results in the trip planner hotel drawer',
    }),
  }
}
